import { createContext, useEffect, useReducer, useState } from 'react';

import axios from '../utils/axios';
import { ACCESS_TOKEN_NAME, ACCESS_TOKEN_USER } from '../const';
import {
  isUserRole,
  isValidToken,
  setSession,
  getUserType,
  getCustomerId,
  normalizeRole,
} from '../utils/jwt';
import { getMimicUser } from '../utils/mimic';

const deriveIdentityFromUser = (user) => ({
  role: normalizeRole(user?.role) || null,
  userType: user?.user_type || null,
  customerId: user?.customer_id || null,
});

// Note: If you're trying to connect JWT to your own backend, don't forget
// to remove the Axios mocks in the `/src/index.js` file.

const INITIALIZE = 'INITIALIZE';
const SIGN_IN = 'SIGN_IN';
const SIGN_OUT = 'SIGN_OUT';
const SIGN_UP = 'SIGN_UP';

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
};

const JWTReducer = (state, action) => {
  switch (action.type) {
    case INITIALIZE:
      return {
        isAuthenticated: action.payload.isAuthenticated,
        isInitialized: true,
        user: action.payload.user,
      };
    case SIGN_IN:
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
      };
    case SIGN_OUT:
      return {
        ...state,
        isAuthenticated: false,
        user: null,
      };

    case SIGN_UP:
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
      };

    default:
      return state;
  }
};

const AuthContext = createContext(null);

const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(JWTReducer, initialState);
  const [userMe, setUserMe] = useState(null);
  const [userType, setUserType] = useState(null);
  const [customerId, setCustomerId] = useState(null);

  useEffect(() => {
    // Clears every trace of a (potentially stale) session. Keeping the
    // role-derived state (userMe/userType/customerId) in lock-step with the
    // real auth result is critical: the login page redirects based on userMe,
    // while the route guards redirect based on isAuthenticated. If those two
    // ever disagree (e.g. an expired token left userMe set while
    // isAuthenticated was false) the app bounces between /auth/sign-in and the
    // role home in an infinite loop that looks like the page reloading.
    const clearSession = () => {
      setSession(ACCESS_TOKEN_NAME, null);
      setSession(ACCESS_TOKEN_USER, null);
      setUserMe(null);
      setUserType(null);
      setCustomerId(null);
    };

    const initialize = async () => {
      try {
        const accessToken =
          window.localStorage.getItem(ACCESS_TOKEN_USER) ||
          window.localStorage.getItem(ACCESS_TOKEN_NAME);

        if (!accessToken || !isValidToken(accessToken)) {
          clearSession();
          dispatch({
            type: INITIALIZE,
            payload: {
              isAuthenticated: false,
              user: null,
            },
          });
          return;
        }

        let isTrueClient = '';
        if (window.localStorage.getItem(ACCESS_TOKEN_USER)) {
          setSession(ACCESS_TOKEN_USER, accessToken);
        } else {
          isTrueClient = '?isTrueClient=true';
          setSession(ACCESS_TOKEN_NAME, accessToken);
        }

        const response = await axios.get(`/api/my-pages/user/my${isTrueClient}`, { skipMimic: true });
        const user = response.data;

        if (user && !user.isActive) {
          if (window.localStorage.getItem(ACCESS_TOKEN_USER)) {
            setSession(ACCESS_TOKEN_USER, null);

            window.location.href = '/admin';
          } else {
            clearSession();
          }

          dispatch({
            type: INITIALIZE,
            payload: {
              isAuthenticated: false,
              user: null,
            },
          });
          return;
        }

        // Only derive the role-based state once we know the session is good,
        // so userMe is never set for a session the guards would reject.
        setUserMe(isUserRole());
        setUserType(getUserType());
        setCustomerId(getCustomerId());

        dispatch({
          type: INITIALIZE,
          payload: {
            isAuthenticated: true,
            user,
          },
        });

        // If admin is mimicking someone, overlay the mimicked user's identity
        // so useAuth() reflects the person being mimicked rather than the
        // admin doing the mimicking.
        if (getMimicUser()) {
          try {
            const mimicResponse = await axios.get('/api/my-pages/user/my');
            const mimicUser = mimicResponse.data;
            const derived = deriveIdentityFromUser(mimicUser);
            setUserMe(derived.role);
            setUserType(derived.userType);
            setCustomerId(derived.customerId);
            dispatch({
              type: INITIALIZE,
              payload: {
                isAuthenticated: true,
                user: mimicUser,
              },
            });
          } catch (_e) {
            // Mimic overlay failed (target no longer exists, etc.).
            // Leave the real-admin state in place so the app stays usable.
          }
        }
      } catch (err) {
        // The token was rejected by the backend (expired/invalid) or the
        // bootstrap call failed. Drop the stale session so we don't keep
        // userMe set while unauthenticated, which is what caused the
        // sign-in <-> guarded-route redirect loop.
        clearSession();
        dispatch({
          type: INITIALIZE,
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    };

    initialize();
  }, []);

  // Passive session watchdog: the JWT's `exp` is a client-side truth we can
  // check without hitting the backend. Re-check every 30s while the user is
  // signed in and, if the token has just expired (idle tab, no requests
  // fired), tear the session down and redirect. Complements the axios 401
  // interceptor which catches the "user clicked something" case.
  useEffect(() => {
    if (!state.isAuthenticated) return undefined;
    const check = () => {
      const accessToken =
        window.localStorage.getItem(ACCESS_TOKEN_USER) ||
        window.localStorage.getItem(ACCESS_TOKEN_NAME);
      if (accessToken && isValidToken(accessToken)) return;
      setSession(ACCESS_TOKEN_NAME, null);
      setSession(ACCESS_TOKEN_USER, null);
      setUserMe(null);
      setUserType(null);
      setCustomerId(null);
      dispatch({ type: SIGN_OUT });
      if (!window.location.pathname.startsWith('/auth/')) {
        window.location.assign('/auth/sign-in');
      }
    };
    const id = window.setInterval(check, 30_000);
    return () => window.clearInterval(id);
  }, [state.isAuthenticated]);

  const signIn = async (email, password) => {
    const response = await axios.post('api/my-pages/auth', {
      email,
      password,
    });

    const { token, user } = response.data;

    // Internal users carry `isActive` (boolean) while customer users carry
    // `status` ('active' | 'inactive'). Only reject when we positively know
    // the account is inactive -- a missing field must never lock anyone out.
    if (user.isActive === false || user.status === 'inactive') {
      throw new Error('Account is inactive');
    }
    setSession(ACCESS_TOKEN_NAME, token);
    // Derive the role from the freshly stored JWT: the token payload always
    // carries a canonical role, whereas the `user` body shape differs between
    // internal users and customer users.
    const role = isUserRole();
    setUserMe(role);
    setUserType(getUserType());
    setCustomerId(getCustomerId());
    dispatch({
      type: SIGN_IN,
      payload: {
        user,
      },
    });
    return role || user.role;
  };

  const setSignIn = async (token, user) => {
    setSession(ACCESS_TOKEN_NAME, token);
    setUserMe(isUserRole());
    setUserType(getUserType());
    setCustomerId(getCustomerId());
    dispatch({
      type: SIGN_IN,
      payload: {
        user,
      },
    });
  };

  const signOut = async () => {
    setSession(ACCESS_TOKEN_NAME, null);
    setUserMe(null);
    setUserType(null);
    setCustomerId(null);
    dispatch({ type: SIGN_OUT });
  };

  const signInAsUser = (token) => {
    setSession(ACCESS_TOKEN_USER, token);
  };
  const signOutAsUser = () => {
    setSession(ACCESS_TOKEN_USER, null);
  };

  const authProviderValue = () => ({
    ...state,
    method: 'jwt',
    signIn,
    signOut,
    userMe,
    setUserMe,
    userType,
    customerId,
    setSignIn,
    signInAsUser,
    signOutAsUser,
  });

  return <AuthContext.Provider value={authProviderValue()}>{children}</AuthContext.Provider>;
};

export { AuthContext, AuthProvider };
